import { ProgramsSideBarDatamodel } from './programs-side-bar-datamodel';
import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import globalSettingsBranches from '../../../stores/global-settings-branches';
import globalConfigStore from '../../../stores/global-config-store';
import { getHebrewYear } from '../../../utils/hebrew-date-utils';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { CustomTag } from '../../../components/CustomTag/CustomTag';
import { CustomExpander } from '../../../components/CustomExpander/CustomExpander';
import { FilterExpanderWithListOfCheckboxes } from '../../../components/CustomExpander/componenets/filter-expander-with-list-of-checkboxes';
import { AbstractProgramSelectionDialog } from './abstract-program-selection-dialog';
import {
  ProgramStudentStatus,
  getProgramStudentStatusDisplayText,
} from '../../../api/types/programs/students/program-student-status';
import { ProgramStatus } from '../../../api/types/programs/program-status';

interface YearOption {
  year: string;
  value: number;
}

interface StatusOption {
  status: ProgramStatus;
  severity: 'success' | 'warning' | 'danger' | 'info';
  checked: boolean;
}

export interface ProgramsSideBarProps {
  onSearch: (filters: ProgramsSideBarDatamodel) => void;
}

export const ProgramsSideBar: React.FC<ProgramsSideBarProps> = observer(
  ({ onSearch }) => {
    const currentYear = new Date().getFullYear();
    const globalYear = globalConfigStore.rootFilter.currentYear;
    const defaultYear = globalYear ?? currentYear;

    const [selectedBranches, setSelectedBranches] = useState<number[]>([]);
    const [selectedYears, setSelectedYears] = useState<number[]>([defaultYear]);
    const [additionalProgramName, setAdditionalProgramName] = useState('');
    const [selectedAbstractProgramIds, setSelectedAbstractProgramIds] =
      useState<number[]>([]);
    const [showAbstractProgramsDialog, setShowAbstractProgramsDialog] =
      useState(false);

    // Sync selectedYears when globalConfigStore.rootFilter.currentYear changes
    useEffect(() => {
      if (globalYear !== undefined) {
        setSelectedYears([globalYear]);
      }
    }, [globalYear]);

    const [statuses, setStatuses] = useState<StatusOption[]>([
      {
        status: ProgramStatus.ACTIVE,
        severity: 'success',
        checked: false,
      },
      {
        status: ProgramStatus.INIT,
        severity: 'info',
        checked: false,
      },
      {
        status: ProgramStatus.FINISHED,
        severity: 'success',
        checked: false,
      },
      {
        status: ProgramStatus.CLOSE,
        severity: 'danger',
        checked: false,
      },
      /*{ status: ProgramStudentStatus.LEFT, severity: 'danger', checked: false },
      {
        status: ProgramStudentStatus.CANCELLED,
        severity: 'danger',
        checked: false,
      },*/
    ]);

    // Generate years array
    const years: YearOption[] = [];
    for (let year = 1980; year <= 2030; year++) {
      years.push({
        year: `${getHebrewYear(year)} (${year} - ${year + 1})`,
        value: year,
      });
    }

    const onStatusChange = (index: number) => {
      const newStatuses = [...statuses];
      newStatuses[index].checked = !newStatuses[index].checked;
      setStatuses(newStatuses);
    };

    const handleBranchToggle = (branchId: number) => {
      setSelectedBranches((prev) =>
        prev.includes(branchId)
          ? prev.filter((b) => b !== branchId)
          : [...prev, branchId],
      );
    };

    const handleYearToggle = (yearStr: string) => {
      const year = parseInt(yearStr);
      setSelectedYears((prev) =>
        prev.includes(year) ? prev.filter((y) => y !== year) : [...prev, year],
      );
    };

    const handleSearch = () => {
      const filters: ProgramsSideBarDatamodel = {
        branchesIds: selectedBranches,
        abstractProgramIds: selectedAbstractProgramIds,
        startYears: selectedYears,
        programsStatuses: statuses
          .filter((s) => s.checked)
          .map((s) => s.status),
        ...(additionalProgramName.trim().length >= 3 && {
          additionalProgramName: additionalProgramName.trim(),
        }),
      };
      onSearch(filters);
    };

    const handleClearAllFilters = () => {
      setSelectedBranches([]);
      setSelectedYears([]);
      setAdditionalProgramName('');
      setSelectedAbstractProgramIds([]);
      setStatuses((prev) =>
        prev.map((status) => ({ ...status, checked: false })),
      );
    };

    const styles = {
      container: {
        height: 'calc(100vh - 111px)',
        background: 'white',
        borderRadius: '8px',
        padding: '16px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column' as const,
      },
      header: {
        fontSize: '1.2rem',
        fontWeight: 'bold',
        color: '#333',
      },
      headerRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        gap: '8px',
      },
      content: {
        flex: 1,
        overflowY: 'auto' as const,
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        paddingRight: '4px',
      },
      statusContainer: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        padding: '8px 0',
      },
      textFilterContainer: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        padding: '8px',
      },
      statusItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        direction: 'rtl' as const,
      },
      buttonContainer: {
        marginTop: '16px',
        padding: '8px',
        borderTop: '1px solid #e9ecef',
      },
      searchButton: {
        width: '100%',
        backgroundColor: '#2196F3',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        padding: '12px',
        fontSize: '1rem',
        fontWeight: 'bold',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
      },
    };

    return (
      <div style={styles.container}>
        <div style={styles.headerRow}>
          <div style={styles.header}>סינונים</div>
          <Button
            icon="pi pi-filter-slash"
            text
            tooltip={'נקה הכל'}
            severity="secondary"
            onClick={handleClearAllFilters}
          />
        </div>
        <div style={styles.content}>
          <FilterExpanderWithListOfCheckboxes
            title="שלוחות"
            items={globalSettingsBranches.branches}
            selectedItems={selectedBranches}
            onItemToggle={handleBranchToggle}
            getItemLabel={(branch) => branch.branchName}
            getItemKey={(branch) => branch.id}
            defaultExpanded={true}
            searchPlaceholder="חפש שלוחה..."
          />

          <FilterExpanderWithListOfCheckboxes
            title="שנת התחלה"
            items={years}
            selectedItems={selectedYears.map(String)}
            onItemToggle={handleYearToggle}
            getItemLabel={(yearOption) => yearOption.year}
            getItemKey={(yearOption) => yearOption.value.toString()}
            searchPlaceholder="חפש שנה..."
          />

          <CustomExpander badge={selectedAbstractProgramIds.length}>
            <div>תוכניות אבסטרקטיות</div>
            <div style={styles.textFilterContainer}>
              <Button
                label="בחירת תוכניות אבסטרקטיות"
                icon="pi pi-list"
                onClick={() => setShowAbstractProgramsDialog(true)}
                outlined
              />
              {selectedAbstractProgramIds.length > 0 && (
                <Button
                  label={`נבחרו ${selectedAbstractProgramIds.length} תוכניות`}
                  icon="pi pi-check"
                  severity="success"
                  text
                  onClick={() => setShowAbstractProgramsDialog(true)}
                />
              )}
              {selectedAbstractProgramIds.length > 0 && (
                <Button
                  label="נקה בחירה"
                  icon="pi pi-times"
                  severity="secondary"
                  text
                  onClick={() => setSelectedAbstractProgramIds([])}
                />
              )}
            </div>
          </CustomExpander>

          <CustomExpander>
            <div>שם תוכנית נוסף</div>
            <div style={styles.textFilterContainer}>
              <InputText
                value={additionalProgramName}
                onChange={(e) => setAdditionalProgramName(e.target.value)}
                placeholder="הקלד לפחות 3 תווים"
                style={{ width: '100%' }}
              />
            </div>
          </CustomExpander>

          <CustomExpander badge={statuses.filter((s) => s.checked).length}>
            <div>סטטוס תוכנית</div>
            <div style={styles.statusContainer}>
              {statuses.map((status, index) => (
                <div key={status.status} style={styles.statusItem}>
                  <Checkbox
                    checked={status.checked}
                    onChange={() => onStatusChange(index)}
                  />
                  <CustomTag
                    width={'70px'}
                    text={ProgramStatus.getDisplayText(status.status)}
                    severity={status.severity}
                  />
                </div>
              ))}
            </div>
          </CustomExpander>
        </div>
        <div style={styles.buttonContainer}>
          <Button
            label="חפש"
            icon="pi pi-search"
            style={styles.searchButton}
            onClick={handleSearch}
          />
        </div>
        <AbstractProgramSelectionDialog
          visible={showAbstractProgramsDialog}
          selectedIds={selectedAbstractProgramIds}
          onHide={() => setShowAbstractProgramsDialog(false)}
          onApprove={(ids) => {
            setSelectedAbstractProgramIds(ids);
            setShowAbstractProgramsDialog(false);
          }}
        />
      </div>
    );
  },
);
